import { useEffect, useReducer, useRef, useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://127.0.0.1:3100';
const APP_WEBSOCKET_PROTOCOL = 'aethercasa-app.v1';
const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const MAX_ACCESS_TOKEN_LENGTH = 8192;

export function appWebSocketBase(apiBase: string): string {
  const url = new URL(apiBase);
  const loopback =
    url.hostname === '127.0.0.1' || url.hostname === 'localhost' || url.hostname === '[::1]';
  if (url.username || url.password) throw new Error('API URL must not contain credentials');
  if (url.protocol !== 'https:' && !(loopback && url.protocol === 'http:')) {
    throw new Error('API URL must use HTTPS outside loopback development');
  }
  if (url.hostname === 'api.aethercasa.app') url.hostname = 'ws.aethercasa.app';
  if (url.hostname === 'staging-api.aethercasa.app') {
    url.hostname = 'staging-ws.aethercasa.app';
  }
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = url.pathname.replace(/\/+$/, '');
  url.search = '';
  url.hash = '';
  return url.toString().replace(/\/$/, '');
}

export interface EntityState {
  state: string;
  attributes: Record<string, unknown>;
  lastChanged?: string;
  lastUpdated?: string;
}

export type WsStatus = 'connecting' | 'open' | 'closed';

export interface WsState {
  entities: Record<string, EntityState>;
  status: WsStatus;
  error: string | null;
}

export type WsAction =
  | { type: 'connecting' }
  | { type: 'open' }
  | { type: 'snapshot'; entities: Record<string, EntityState> }
  | { type: 'state_update'; entityId: string; newState: EntityState }
  | { type: 'closed'; error?: string };

export function wsReducer(state: WsState, action: WsAction): WsState {
  switch (action.type) {
    case 'connecting':
      return { ...state, status: 'connecting', error: null };
    case 'open':
      return { ...state, status: 'open', error: null };
    case 'snapshot':
      return { ...state, entities: action.entities };
    case 'state_update':
      return {
        ...state,
        entities: { ...state.entities, [action.entityId]: action.newState },
      };
    case 'closed':
      return {
        ...state,
        status: 'closed',
        ...(action.error !== undefined ? { error: action.error } : {}),
      };
  }
}

export function useAppSocket(
  homeId: string | null,
  getToken: () => string | null,
  onTokenExpired: () => Promise<string | null>,
) {
  const [state, dispatch] = useReducer(wsReducer, {
    entities: {},
    status: 'connecting',
    error: null,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(1000);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = useRef(false);
  const connectionIdRef = useRef(0);

  const connect = useCallback(
    async (tokenOverride?: string) => {
      if (!homeId || unmountedRef.current || wsRef.current) return;

      const token = tokenOverride ?? getToken();
      if (!token) return;
      if (token.length > MAX_ACCESS_TOKEN_LENGTH || !JWT_SHAPE.test(token)) {
        dispatch({ type: 'closed', error: 'Invalid access token' });
        return;
      }

      dispatch({ type: 'connecting' });
      const url = `${appWebSocketBase(API_BASE)}/ws/app?home=${encodeURIComponent(homeId)}`;
      const ws = new WebSocket(url, [APP_WEBSOCKET_PROTOCOL, token]);
      const connectionId = ++connectionIdRef.current;
      wsRef.current = ws;

      ws.onopen = () => {
        if (unmountedRef.current || wsRef.current !== ws) return;
        dispatch({ type: 'open' });
        backoffRef.current = 1000;
      };

      ws.onmessage = async (evt: MessageEvent) => {
        if (unmountedRef.current || wsRef.current !== ws) return;
        try {
          const msg = JSON.parse(evt.data as string) as {
            type: string;
            entities?: Record<string, EntityState>;
            entityId?: string;
            newState?: EntityState;
          };
          if (msg.type === 'snapshot' && msg.entities) {
            dispatch({ type: 'snapshot', entities: msg.entities });
          } else if (msg.type === 'state.update' && msg.entityId && msg.newState) {
            dispatch({ type: 'state_update', entityId: msg.entityId, newState: msg.newState });
          } else if (msg.type === 'token_expired') {
            wsRef.current = null;
            ws.close();
            dispatch({ type: 'closed' });
            const newToken = await onTokenExpired();
            if (newToken && !unmountedRef.current && connectionIdRef.current === connectionId) {
              void connect(newToken);
            }
          } else if (msg.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong' }));
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onerror = () => {
        if (unmountedRef.current || wsRef.current !== ws) return;
        dispatch({ type: 'closed', error: 'WebSocket error' });
      };

      ws.onclose = () => {
        if (unmountedRef.current || wsRef.current !== ws) return;
        wsRef.current = null;
        dispatch({ type: 'closed' });
        const delay = Math.min(backoffRef.current, 30000);
        backoffRef.current = delay * 2;
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          if (!unmountedRef.current) void connect();
        }, delay);
      };
    },
    [homeId, getToken, onTokenExpired],
  );

  useEffect(() => {
    unmountedRef.current = false;
    if (homeId) {
      void connect();
    } else {
      dispatch({ type: 'closed' });
    }
    return () => {
      unmountedRef.current = true;
      connectionIdRef.current += 1;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const ws = wsRef.current;
      wsRef.current = null;
      ws?.close();
    };
  }, [homeId, connect]);

  const patchEntity = useCallback((entityId: string, newState: EntityState) => {
    dispatch({ type: 'state_update', entityId, newState });
  }, []);

  return { ...state, patchEntity };
}
